import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faStar, 
  faThumbsUp, 
  faThumbsDown,
  faComment,
  faPaperPlane,
  faCheck,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { apiService, FeedbackData } from '../utils/api';

interface AnalysisFeedbackProps {
  analysisId: string;
  analysisType: string;
  onFeedbackSubmitted?: () => void;
}

const AnalysisFeedback: React.FC<AnalysisFeedbackProps> = ({
  analysisId,
  analysisType,
  onFeedbackSubmitted
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [feedback, setFeedback] = useState<FeedbackData>({
    overallRating: 0,
    categoryRatings: {
      accuracy: 0,
      clarity: 0,
      usefulness: 0,
      completeness: 0
    },
    comment: '',
    wouldRecommend: null,
    mostHelpful: '',
    improvements: ''
  });

  const handleStarClick = (rating: number, category?: keyof FeedbackData['categoryRatings']) => {
    if (category) {
      setFeedback(prev => ({
        ...prev,
        categoryRatings: {
          ...prev.categoryRatings,
          [category]: rating
        }
      }));
    } else {
      setFeedback(prev => ({ ...prev, overallRating: rating }));
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      // Submit feedback using API service
      const response = await apiService.submitFeedback(analysisId, feedback);
      
      if (response.success) {
        setIsSubmitted(true);
        if (onFeedbackSubmitted) {
          onFeedbackSubmitted();
        }
      } else {
        throw new Error(response.error || 'Feedback submission failed');
      }
    } catch (error) {
      console.error('Feedback submission failed:', error);
      alert('Değerlendirme gönderilirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const StarRating = ({ 
    rating, 
    onRate, 
    size = '1.25rem',
    color = '#f59e0b'
  }: { 
    rating: number; 
    onRate: (rating: number) => void;
    size?: string;
    color?: string;
  }) => (
    <div style={{ display: 'flex', gap: '0.25rem' }}>
      {[1, 2, 3, 4, 5].map((star) => (
        <FontAwesomeIcon
          key={star}
          icon={faStar}
          style={{
            fontSize: size,
            color: star <= rating ? color : '#e5e7eb',
            cursor: 'pointer',
            transition: 'color 0.2s ease'
          }}
          onClick={() => onRate(star)}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = star <= rating ? color : '#f3f4f6';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = star <= rating ? color : '#e5e7eb';
          }}
        />
      ))}
    </div>
  );

  const containerStyle: React.CSSProperties = {
    background: 'white',
    borderRadius: '16px',
    padding: '1.5rem',
    marginBottom: '1.5rem',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    border: '1px solid rgba(168, 85, 247, 0.1)',
    transition: 'all 0.3s ease'
  };

  const categoryStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.75rem 0',
    borderBottom: '1px solid #f3f4f6'
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 16px',
    border: '1px solid rgba(168, 85, 247, 0.3)',
    borderRadius: '8px',
    fontSize: '1rem',
    resize: 'vertical',
    fontFamily: 'inherit',
    minHeight: '80px'
  };

  const buttonStyle: React.CSSProperties = {
    padding: '12px 24px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: '600',
    transition: 'all 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  };

  if (isSubmitted) {
    return (
      <div style={{
        ...containerStyle,
        textAlign: 'center',
        background: 'linear-gradient(135deg, #d1fae5, #a7f3d0)'
      }}>
        <FontAwesomeIcon 
          icon={faCheck} 
          style={{ fontSize: '2rem', color: '#059669', marginBottom: '1rem' }} 
        />
        <h3 style={{ color: '#065f46', marginBottom: '0.5rem' }}>
          Değerlendirmeniz Alındı!
        </h3>
        <p style={{ color: '#047857', margin: 0 }}>
          Geri bildiriminiz için teşekkür ederiz. Sizin gibi değerli kullanıcılarımızın görüşleri 
          hizmet kalitemizi artırmamıza yardımcı oluyor.
        </p>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div 
        style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          cursor: 'pointer',
          marginBottom: isExpanded ? '1.5rem' : 0
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#374151', margin: 0 }}>
            Bu Analizi Değerlendirin
          </h3>
          <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: '0.25rem 0 0 0' }}>
            Deneyiminizi paylaşın ve hizmetimizi geliştirmemize yardımcı olun
          </p>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <StarRating 
            rating={feedback.overallRating} 
            onRate={(rating) => handleStarClick(rating)}
            size="1rem"
          />
          <FontAwesomeIcon 
            icon={faComment} 
            style={{ 
              color: '#8b5cf6', 
              fontSize: '1.25rem',
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s ease'
            }} 
          />
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div>
          {/* Overall Rating */}
          <div style={{ marginBottom: '2rem' }}>
            <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>
              Genel Memnuniyet
            </h4>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <StarRating 
                rating={feedback.overallRating} 
                onRate={(rating) => handleStarClick(rating)}
              />
              <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                {feedback.overallRating > 0 && (
                  feedback.overallRating >= 4 ? 'Mükemmel' :
                  feedback.overallRating >= 3 ? 'İyi' :
                  feedback.overallRating >= 2 ? 'Orta' : 'Geliştirilmeli'
                )}
              </span>
            </div>
          </div>

          {/* Category Ratings */}
          <div style={{ marginBottom: '2rem' }}>
            <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#374151', marginBottom: '1rem' }}>
              Detaylı Değerlendirme
            </h4>
            
            <div style={categoryStyle}>
              <span style={{ color: '#374151', fontSize: '0.875rem' }}>Doğruluk ve Hassaslık</span>
              <StarRating 
                rating={feedback.categoryRatings.accuracy} 
                onRate={(rating) => handleStarClick(rating, 'accuracy')}
                size="1rem"
              />
            </div>
            
            <div style={categoryStyle}>
              <span style={{ color: '#374151', fontSize: '0.875rem' }}>Anlaşılabilirlik</span>
              <StarRating 
                rating={feedback.categoryRatings.clarity} 
                onRate={(rating) => handleStarClick(rating, 'clarity')}
                size="1rem"
              />
            </div>
            
            <div style={categoryStyle}>
              <span style={{ color: '#374151', fontSize: '0.875rem' }}>Faydalılık</span>
              <StarRating 
                rating={feedback.categoryRatings.usefulness} 
                onRate={(rating) => handleStarClick(rating, 'usefulness')}
                size="1rem"
              />
            </div>
            
            <div style={{ ...categoryStyle, borderBottom: 'none' }}>
              <span style={{ color: '#374151', fontSize: '0.875rem' }}>Eksiksizlik</span>
              <StarRating 
                rating={feedback.categoryRatings.completeness} 
                onRate={(rating) => handleStarClick(rating, 'completeness')}
                size="1rem"
              />
            </div>
          </div>

          {/* Recommendation */}
          <div style={{ marginBottom: '1.5rem' }}>
            <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>
              Kairoscope'u Başkalarına Önerir misiniz?
            </h4>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                onClick={() => setFeedback(prev => ({ ...prev, wouldRecommend: true }))}
                style={{
                  ...buttonStyle,
                  background: feedback.wouldRecommend === true ? '#059669' : 'transparent',
                  color: feedback.wouldRecommend === true ? 'white' : '#059669',
                  border: '1px solid #059669'
                }}
              >
                <FontAwesomeIcon icon={faThumbsUp} />
                <span>Evet</span>
              </button>
              <button
                onClick={() => setFeedback(prev => ({ ...prev, wouldRecommend: false }))}
                style={{
                  ...buttonStyle,
                  background: feedback.wouldRecommend === false ? '#dc2626' : 'transparent',
                  color: feedback.wouldRecommend === false ? 'white' : '#dc2626',
                  border: '1px solid #dc2626'
                }}
              >
                <FontAwesomeIcon icon={faThumbsDown} />
                <span>Hayır</span>
              </button>
            </div>
          </div>

          {/* Most Helpful Section */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ fontSize: '1rem', fontWeight: '600', color: '#374151', marginBottom: '0.75rem', display: 'block' }}>
              En Faydalı Bulduğunuz Bölüm
            </label>
            <select
              value={feedback.mostHelpful}
              onChange={(e) => setFeedback(prev => ({ ...prev, mostHelpful: e.target.value }))}
              style={{
                ...inputStyle,
                minHeight: 'auto',
                height: '48px'
              }}
            >
              <option value="">Seçiniz...</option>
              <option value="results">Numerolojik Sonuçlar</option>
              <option value="insights">Detaylı İçgörüler</option>
              <option value="recommendations">Kişisel Öneriler</option>
              <option value="summary">Genel Değerlendirme</option>
              <option value="overall">Tüm Rapor</option>
            </select>
          </div>

          {/* Comment */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ fontSize: '1rem', fontWeight: '600', color: '#374151', marginBottom: '0.75rem', display: 'block' }}>
              Yorumunuz (İsteğe Bağlı)
            </label>
            <textarea
              value={feedback.comment}
              onChange={(e) => setFeedback(prev => ({ ...prev, comment: e.target.value }))}
              placeholder="Analiz hakkındaki düşüncelerinizi, beğendiğiniz veya geliştirilmesini istediğiniz noktaları paylaşın..."
              style={inputStyle}
            />
          </div>

          {/* Improvements */}
          <div style={{ marginBottom: '2rem' }}>
            <label style={{ fontSize: '1rem', fontWeight: '600', color: '#374151', marginBottom: '0.75rem', display: 'block' }}>
              Geliştirilmesini İstediğiniz Alanlar
            </label>
            <textarea
              value={feedback.improvements}
              onChange={(e) => setFeedback(prev => ({ ...prev, improvements: e.target.value }))}
              placeholder="Hangi konularda daha detaylı bilgi almak isterdiniz? Ne eklenebilir?"
              style={inputStyle}
            />
          </div>

          {/* Submit Button */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
            <button
              onClick={() => setIsExpanded(false)}
              style={{
                ...buttonStyle,
                background: 'transparent',
                color: '#6b7280',
                border: '1px solid #d1d5db'
              }}
            >
              İptal
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || feedback.overallRating === 0}
              style={{
                ...buttonStyle,
                background: feedback.overallRating === 0 ? '#9ca3af' : 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                color: 'white',
                cursor: feedback.overallRating === 0 ? 'not-allowed' : 'pointer',
                opacity: feedback.overallRating === 0 ? 0.6 : 1
              }}
            >
              <FontAwesomeIcon icon={isSubmitting ? faSpinner : faPaperPlane} spin={isSubmitting} />
              <span>{isSubmitting ? 'Gönderiliyor...' : 'Değerlendirmeyi Gönder'}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalysisFeedback; 